/**
 * Terms of Service — public route at /legal/terms
 * Used as the "Terms of Service URL" in Google Play + App Store listings
 * and linked from the mobile app's Profile → About screen.
 */
import { Link } from "react-router-dom";

const LAST_UPDATED = "August 25, 2026";
const PUBLISHER = "Sashikunja Educational Services LLP";
const APP_NAME = "Kampus 365";
const PLATFORM_DOMAIN = "edukampus.com";
const SUPPORT_EMAIL = "klickapps2026@gmail.com";
const GOVERNING_JURISDICTION = "Gorakhpur, Uttar Pradesh, India";

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="terms-page">
      <div className="mx-auto max-w-3xl px-6 py-16">
        <div className="mb-10">
          <Link
            to="/"
            className="text-xs uppercase tracking-widest text-slate-500 hover:text-slate-900"
            data-testid="back-home-link"
          >
            ← Back to Kampus 365
          </Link>
          <h1 className="mt-4 text-4xl font-bold tracking-tight" data-testid="terms-heading">
            Terms of Service
          </h1>
          <p className="mt-2 text-sm text-slate-500">Last updated: {LAST_UPDATED}</p>
        </div>

        <section className="max-w-none text-slate-700 leading-relaxed space-y-4 [&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:text-slate-900 [&_h2]:mt-10 [&_h2]:mb-3 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-slate-900 [&_h3]:mt-6 [&_h3]:mb-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1 [&_a]:text-blue-600 [&_a]:underline hover:[&_a]:text-blue-800 [&_strong]:text-slate-900">
          <p>
            These Terms of Service (“Terms”) govern your access to and use of
            the <strong>{APP_NAME}</strong> platform, mobile application, and
            website at {PLATFORM_DOMAIN} (collectively, the “Service”),
            operated by <strong>{PUBLISHER}</strong> (“we,” “us,” or “our”).
            By creating an account, signing in, or using the Service, you
            agree to these Terms. If you do not agree, do not use the Service.
          </p>

          <h2>1. Who Can Use the Service</h2>
          <p>
            The Service is intended for use by schools and their authorised
            users (administrators, teachers, parents, and students). If you
            are under 18, you may use the Service only through an account
            provisioned for you by your school and with your parent or legal
            guardian's consent. School administrators are responsible for
            ensuring appropriate consent has been obtained before creating
            accounts for children.
          </p>

          <h2>2. Accounts &amp; Security</h2>
          <ul>
            <li>You are responsible for keeping your password confidential.</li>
            <li>You are responsible for all activity that occurs under your account.</li>
            <li>Notify us immediately at <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> if you suspect unauthorised access.</li>
            <li>We may suspend or terminate accounts that violate these Terms or applicable law.</li>
          </ul>

          <h2>3. Acceptable Use</h2>
          <p>You agree NOT to:</p>
          <ul>
            <li>Impersonate any person or misrepresent your affiliation with a school.</li>
            <li>Upload, post, or share content that is unlawful, defamatory, obscene, or infringing.</li>
            <li>Attempt to gain unauthorised access to accounts, data, or infrastructure.</li>
            <li>Reverse-engineer, decompile, or otherwise attempt to extract source code.</li>
            <li>Use the Service to harass, threaten, or harm any student, parent, or staff member.</li>
            <li>Scrape, bulk-download, or otherwise systematically extract data without written permission.</li>
            <li>Introduce malware, viruses, or any code intended to damage the Service.</li>
          </ul>

          <h2>4. School's Role &amp; Content</h2>
          <p>
            {APP_NAME} is a hosted service provided to schools. Each school
            (the “Institution”) is the data controller of the personal
            information of its students, parents, and staff. As a user, you
            grant your Institution and {PUBLISHER} a limited licence to
            store, display, and transmit any content you post (homework,
            notices, chat messages, photos, files) solely for the purpose of
            operating the Service. You retain ownership of your content.
          </p>

          <h2>5. Fees, Payments &amp; Refunds</h2>
          <p>
            The Service may be used to view and pay school fees. All fee
            amounts, structures, and due dates are set and managed by the
            Institution. Payment processing is handled by regulated
            third-party payment gateways. Refund requests for school fees
            must be raised with the Institution directly. Platform
            subscription fees paid by the Institution to {PUBLISHER} are
            governed by a separate commercial agreement.
          </p>

          <h2>6. Intellectual Property</h2>
          <p>
            The Service, including all software, design, text, graphics, and
            trademarks, is owned by {PUBLISHER} and its licensors and is
            protected by intellectual property laws. You are granted a
            limited, revocable, non-exclusive, non-transferable licence to
            use the Service in accordance with these Terms. All rights not
            expressly granted are reserved.
          </p>

          <h2>7. Third-Party Services</h2>
          <p>
            The Service may integrate with third-party services (payment
            gateways, SMS/email providers, cloud storage, push-notification
            providers). Your use of those services is governed by their own
            terms; {PUBLISHER} is not responsible for their content,
            practices, or availability.
          </p>

          <h2>8. Service Availability &amp; Modifications</h2>
          <p>
            We strive to keep the Service available at all times but do not
            guarantee uninterrupted access. We may modify, suspend, or
            discontinue features with reasonable notice. We may release
            updates that add, change, or remove functionality; continued use
            after an update constitutes acceptance of the change.
          </p>

          <h2>9. Termination</h2>
          <p>
            You may stop using the Service at any time. We may suspend or
            terminate your access if you violate these Terms, if your
            school's subscription lapses, or if legally required. Upon
            termination, your right to use the Service ceases immediately;
            portions that by their nature should survive (Section 6, 10, 11,
            and 12) will continue in effect.
          </p>

          <h2>10. Disclaimers</h2>
          <p>
            The Service is provided on an <strong>"AS IS" and "AS AVAILABLE"</strong>
            basis, without warranties of any kind, whether express or
            implied. We do not warrant that the Service will be error-free,
            secure, or uninterrupted. Educational and financial information
            displayed in the app is provided by the Institution; while we
            take reasonable care to render it accurately, {PUBLISHER} is not
            responsible for the correctness of school-provided content.
          </p>

          <h2>11. Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by law, {PUBLISHER}, its
            affiliates, officers, and employees shall not be liable for any
            indirect, incidental, special, consequential, or punitive
            damages, or for any loss of profits, revenues, data, or use,
            arising out of or in connection with the Service. Our aggregate
            liability for any claim arising out of or relating to these
            Terms shall not exceed the amount you paid us in the 12 months
            preceding the claim, or INR 5,000 — whichever is greater.
          </p>

          <h2>12. Indemnification</h2>
          <p>
            You agree to indemnify and hold harmless {PUBLISHER} from any
            claim, damages, losses, liabilities, and expenses (including
            reasonable legal fees) arising out of your use of the Service,
            your content, or your breach of these Terms.
          </p>

          <h2>13. Governing Law &amp; Dispute Resolution</h2>
          <p>
            These Terms are governed by the laws of the Republic of India.
            Any dispute arising out of or relating to these Terms or the
            Service shall be subject to the exclusive jurisdiction of the
            courts at {GOVERNING_JURISDICTION}. The parties agree to first
            attempt to resolve any dispute in good faith through direct
            negotiation.
          </p>

          <h2>14. Changes to These Terms</h2>
          <p>
            We may update these Terms from time to time. Material changes
            will be notified by in-app notice or email at least 30 days
            before they take effect. Continued use after the effective date
            constitutes acceptance of the updated Terms.
          </p>

          <h2>15. Contact Us</h2>
          <address className="not-italic">
            <strong>{PUBLISHER}</strong>
            <br />
            Product: {APP_NAME}
            <br />
            Support: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
            <br />
            Web: <a href="https://edukampus.com" target="_blank" rel="noopener noreferrer">https://edukampus.com</a>
          </address>
        </section>

        <div className="mt-12 border-t border-slate-200 pt-6 text-sm text-slate-500 flex flex-wrap gap-4">
          <Link to="/legal/privacy" className="hover:text-slate-900" data-testid="privacy-link">
            Privacy Policy
          </Link>
          <span>·</span>
          <Link to="/" className="hover:text-slate-900" data-testid="home-link">
            Home
          </Link>
        </div>
      </div>
    </div>
  );
}
